/* Mirror state; never recreate checkout, pricing, quantity or preorder submission. */
(() => {
  if (customElements.get('bh-sticky-purchase')) return;

  class BhStickyPurchase extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.variants = JSON.parse(this.querySelector('[data-bh-sticky-data]').textContent).variants;
      this.purchaseButton = this.querySelector('[data-bh-sticky-submit]');
      this.label = this.querySelector('[data-bh-sticky-label]');
      this.variantLabel = this.querySelector('[data-bh-sticky-variant]');
      this.price = this.querySelector('[data-bh-sticky-price]');
      this.image = this.querySelector('[data-bh-sticky-image]');
      this.scope = this.closest('.shopify-section') || document.querySelector('main');
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      };
      this.purchaseButton.addEventListener('click', () => this.purchase(), options);
      this.querySelector('.bh-sticky-purchase__details').addEventListener('click', event => {
        const destination = this.scope?.querySelector('#bh-buy, .product-info');
        if (destination) {
          event.preventDefault();
          destination.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        }
      }, options);
      document.addEventListener('variant:change', event => {
        // form.id may be shadowed by Shopify's input[name="id"].
        if (event.target.getAttribute?.('id') !== this.dataset.formId && event.detail?.formId !== this.dataset.formId) return;
        this.variantPending = false;
        this.schedule();
      }, options);
      document.addEventListener('change', event => {
        if (event.target.getAttribute?.('form') === this.dataset.formId && event.target.matches('[data-option-position]')) {
          this.variantPending = true;
          this.purchaseButton.disabled = true;
        }
        this.schedule();
      }, { ...options, capture: true });
      document.addEventListener('input', this.schedule, options);
      document.addEventListener('variant:add', this.schedule, options);
      document.addEventListener('cart:error', event => {
        if (event.target.getAttribute?.('id') !== this.dataset.formId) return;
        this.form?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.schedule();
      }, options);
      for (const type of ['dialog:before-show', 'dialog:after-hide']) document.addEventListener(type, this.schedule, { ...options, capture: true });
      window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      window.addEventListener('resize', this.schedule, { ...options, passive: true });
      window.visualViewport?.addEventListener('resize', this.schedule, { ...options, passive: true });
      this.sectionObserver = new MutationObserver(records => {
        if (records.some(record => !this.contains(record.target))) this.schedule();
      });
      this.sectionObserver.observe(this.scope, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'hidden', 'style', 'class', 'value'] });
      this.pageObserver = new MutationObserver(this.schedule);
      this.pageObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      this.pageObserver.observe(document.body, { childList: true });
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.resizeObserver.observe(this);
      this.previewObserver = new MutationObserver(this.schedule);
      document.body.classList.add('bh-has-sticky-purchase');
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.sectionObserver?.disconnect();
      this.pageObserver?.disconnect();
      this.previewObserver?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      document.body.style.removeProperty('--bh-sticky-space');
      document.body.style.removeProperty('--bh-preview-space');
      document.body.classList.remove('bh-has-sticky-purchase');
    }

    rendered(element) {
      if (!element || element.hidden) return false;
      const style = getComputedStyle(element), rect = element.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
    }

    sourceButton() {
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return null;
      // The app hides the original native button with transform:scale(0).
      // Use the visible app button first, without assigning our own selling plan.
      const preorder = this.form.querySelector('[data-essential-preorder-element="button"]');
      if (this.rendered(preorder)) return preorder;
      return [...this.form.querySelectorAll('buy-buttons button[type="submit"]')].find(button => this.rendered(button)) || null;
    }

    selectedVariant() {
      const id = this.form?.elements.namedItem('id')?.value;
      return this.variants.find(variant => String(variant.id) === String(id));
    }

    setText(element, value) {
      if (element.textContent !== value) element.textContent = value;
    }

    previewInset() {
      const preview = document.querySelector('#PBarNextFrameWrapper, #preview-bar-iframe, #ShopifyPreviewBar');
      if (this.preview !== preview) {
        if (this.preview) this.resizeObserver.unobserve(this.preview);
        this.previewObserver.disconnect();
        this.preview = preview;
        if (preview) {
          this.resizeObserver.observe(preview);
          this.previewObserver.observe(preview, { attributes: true, attributeFilter: ['style', 'hidden', 'class'] });
        }
      }
      if (!this.rendered(preview)) return 0;
      const rect = preview.getBoundingClientRect();
      return rect.bottom >= innerHeight - 2 ? Math.max(0, innerHeight - rect.top) : 0;
    }

    update() {
      if (!this.isConnected) return;
      const source = this.sourceButton(), variant = this.selectedVariant();
      const busy = source?.getAttribute('aria-busy') === 'true';
      const disabled = !variant?.available || !source || source.disabled || source.getAttribute('aria-disabled') === 'true' || this.variantPending || busy;
      if (this.purchaseButton.disabled !== Boolean(disabled)) this.purchaseButton.disabled = Boolean(disabled);
      if (this.purchaseButton.getAttribute('aria-busy') !== String(busy)) this.purchaseButton.setAttribute('aria-busy', String(busy));
      let label = source?.textContent.trim().replace(/\s+/g, ' ') || this.dataset.addLabel;
      if (!variant) label = this.dataset.unavailableLabel;
      else if (!variant.available) label = this.dataset.soldOutLabel;
      this.setText(this.label, label);
      this.setText(this.price, variant?.price || '');
      const quantity = Number(this.form?.elements.namedItem('quantity')?.value || 1);
      const title = variant && variant.title !== 'Default Title' ? variant.title : '';
      this.setText(this.variantLabel, [title, quantity > 1 ? `${this.dataset.quantityLabel}: ${quantity}` : ''].filter(Boolean).join(' · '));
      if (variant?.image && this.image.getAttribute('src') !== variant.image) this.image.src = variant.image;
      this.image.hidden = !variant?.image;

      const inset = this.previewInset();
      if (this.inset !== inset) {
        this.inset = inset;
        document.body.style.setProperty('--bh-preview-space', `${inset}px`);
      }
      const rect = (source || this.form)?.getBoundingClientRect();
      const header = document.querySelector('store-header, .shopify-section--header');
      const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      const visibleMainButton = rect && rect.bottom > headerBottom + 5 && rect.top < innerHeight - inset - 8;
      const keyboard = window.visualViewport && innerHeight - window.visualViewport.height > 150 && document.activeElement?.matches('input:not([type=radio]):not([type=checkbox]), textarea, [contenteditable=true]');
      const overlay = document.documentElement.classList.contains('lock') || document.querySelector('dialog[open], cart-drawer[open], quick-buy-modal[open], header-sidebar[open], header-search[open], .pswp--open');
      const visible = Boolean(this.form && !visibleMainButton && !overlay && !keyboard && inset < innerHeight / 2);
      if (this.hidden === visible) this.hidden = !visible;
      const space = visible ? Math.ceil(this.getBoundingClientRect().height) : 0;
      if (this.space !== space) {
        this.space = space;
        document.body.style.setProperty('--bh-sticky-space', `${space}px`);
      }
    }

    purchase() {
      // Resolve again at click time: variant changes replace the source button.
      this.update();
      const source = this.sourceButton(), variant = this.selectedVariant();
      if (this.purchaseButton.disabled || !variant?.available || !source) return;
      if (!this.form.checkValidity()) {
        this.form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.form.reportValidity();
        return;
      }
      this.purchaseButton.disabled = true;
      source.click();
      this.schedule();
    }
  }
  customElements.define('bh-sticky-purchase', BhStickyPurchase);
})();
